import React from 'react';
import { connect } from 'react-redux';

import {
  workflowSetArchiveLocation,
  workflowSetArchiveLocationFilePath,
} from '~/actions/workflow';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { FormattedMessage, useIntl } from 'react-intl';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { withInputError } from '~/ui/hocs/form';
import { getArchiveLocationPath, validateFolderPath } from '~/utils/folders';
import { testValidFieldsForArchiveSubFolder } from '~/utils/ticket';
import { pathObjectToString } from '~/utils/workflow';

import Section from '../Section';
import ArchiveLocationInput from './ArchiveLocationInput';
import ArchiveLocationPath from './ArchiveLocationPath';

function LocationPreview({
  archiveLocation,
  error,
  fields,
  filePathPreview,
  id,
}) {
  const filePathString = pathObjectToString(archiveLocation.filePath, fields);
  const fileNameString = pathObjectToString(archiveLocation.fileName, fields);

  const finalOutput = filePathString
    ? `${filePathPreview}/${filePathString}/${fileNameString}`
    : `${filePathPreview}/${fileNameString}`;

  return (
    <FlexLayout flexDirection="column" space={2}>
      <FlexLayout
        id={id}
        alignItems="center"
        px={4}
        py={2}
        space={2}
        sx={{
          bg: 'black-alpha-04',
          border: error ? 'border' : 'none',
          borderColor: error ? 'red-400' : 'none',
          borderRadius: 'm',
        }}
      >
        <Icon icon="folder" />
        <Text color="gray-600" variant="xs-dense-medium">
          {finalOutput}
        </Text>
      </FlexLayout>
    </FlexLayout>
  );
}

function validateArchiveLocationPreview({
  filePathPreview,
  fields,
  archiveLocation,
  intl,
}) {
  const filePathString = pathObjectToString(archiveLocation.filePath, fields);
  const fileNameString = pathObjectToString(archiveLocation.fileName, fields);

  const finalOutput = filePathString
    ? `${filePathPreview}/${filePathString}/${fileNameString}`
    : `${filePathPreview}/${fileNameString}`;

  return validateFolderPath(finalOutput)
    ? null
    : intl.formatMessage({
        id: 'ArchiveLocation.invalidFolderPathError',
        defaultMessage: 'Invalid folder path.',
        description: 'Error message for invalid folder path',
      });
}

function formatNamingOutput(string) {
  return string.replace(/\//g, '');
}

function ArchiveLocation({
  //connected
  archiveLocation,
  fields,
  workflowSetArchiveLocation,
  workflowSetArchiveLocationFilePath,
  folderPath,
}) {
  const intl = useIntl();
  const LocationPreviewWithError = withInputError(LocationPreview, (props) =>
    validateArchiveLocationPreview({ ...props, intl }),
  );

  const { pilotFolderId, filePath, fileName } = archiveLocation;

  const placeholder = (
    <Text color="gray-600" sx={{ fontStyle: 'italic' }}>
      {intl.formatMessage({
        id: 'ArchiveLocation.folderPlaceholder',
        defaultMessage: '[Folder placeholder]',
        description: 'Placeholder text for folder',
      })}
    </Text>
  );

  const filePathPreview =
    (folderPath && folderPath[pilotFolderId]) || placeholder;

  return (
    <Section
      description={
        <Text color="gray-600" variant="xs-dense-medium">
          <FormattedMessage
            id="ArchiveLocation.sectionDescriptionComplete"
            defaultMessage='Specify the destination and filename. Paths and filenames can be dynamic, generated by fields.{br}Use <strong># to access dynamic tags</strong> and separate sub folders by <strong>using "/" between tags</strong>.'
            description="Complete description for archive location section with dynamic tag instructions"
            values={{
              br: <br />,
              strong: (chunks) => (
                <Text color="black" variant="xs-dense-medium">
                  {chunks}
                </Text>
              ),
            }}
          />
        </Text>
      }
      icon="field-folder"
      title={intl.formatMessage({
        id: 'ArchiveLocation.sectionTitle',
        defaultMessage: 'Archive Location',
        description: 'Title for archive location section',
      })}
    >
      <ArchiveLocationPath
        folderPath={folderPath}
        folderId={pilotFolderId}
        onChange={workflowSetArchiveLocationFilePath}
        errorMessage={
          folderPath && folderPath[pilotFolderId]
            ? ''
            : intl.formatMessage({
                id: 'ArchiveLocation.invalidFolderPathError',
                defaultMessage: 'Invalid folder path.',
                description: 'Error message for invalid folder path',
              })
        }
      />
      <ArchiveLocationInput
        archiveLocation={filePath}
        fields={fields}
        id="workflow--settings-sub-folder-entry"
        label={intl.formatMessage({
          id: 'ArchiveLocation.subFolderLabel',
          defaultMessage: 'Sub folder',
          description: 'Label for sub folder input',
        })}
        placeholder={intl.formatMessage({
          id: 'ArchiveLocation.specifySubFolderPlaceholder',
          defaultMessage: 'Specify sub folder',
          description: 'Placeholder for sub folder input',
        })}
        onChange={(filePath) => workflowSetArchiveLocation({ filePath })}
        errorMessage={
          testValidFieldsForArchiveSubFolder(filePath, fields)
            ? ''
            : intl.formatMessage({
                id: 'ArchiveLocation.invalidSubFolderError',
                defaultMessage: 'Invalid sub folder.',
                description: 'Error message for invalid sub folder',
              })
        }
      />
      <ArchiveLocationInput
        archiveLocation={fileName}
        fields={fields}
        id="workflow--settings-naming-format-entry"
        label={intl.formatMessage({
          id: 'ArchiveLocation.namingFormatLabel',
          defaultMessage: 'Naming format',
          description: 'Label for naming format input',
        })}
        placeholder={intl.formatMessage({
          id: 'ArchiveLocation.specifyFileNamePlaceholder',
          defaultMessage: 'Specify file name',
          description: 'Placeholder for file name input',
        })}
        formatOutput={formatNamingOutput}
        onChange={(fileName) => workflowSetArchiveLocation({ fileName })}
        errorMessage={
          testValidFieldsForArchiveSubFolder(fileName, fields)
            ? ''
            : intl.formatMessage({
                id: 'ArchiveLocation.invalidNamingFormatError',
                defaultMessage: 'Invalid naming format.',
                description: 'Error message for invalid naming format',
              })
        }
      />
      <LocationPreviewWithError
        archiveLocation={archiveLocation}
        fields={fields}
        filePathPreview={filePathPreview}
        id="workflow--settings-naming-convention-preview"
      />
    </Section>
  );
}

function mapStateToProps({ workflow, client }) {
  // only include fields that are assigned and have valid types defined.
  return {
    archiveLocation: workflow.settings.archiveLocation,
    fields: workflow.fields,
    folderPath: getArchiveLocationPath(
      client.folderTree,
      workflow.settings.archiveLocation.pilotFolderId,
    ),
  };
}

export default connect(mapStateToProps, {
  workflowSetArchiveLocation,
  workflowSetArchiveLocationFilePath,
})(ArchiveLocation);
